import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { COMPARISON, BRAND } from "@/lib/constants";
import { Check, X, Minus } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

function CellContent({ value }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#D8FFB8]">
        <Check className="h-4.5 w-4.5 text-[#20611B] stroke-[2.5]" />
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#FEE2E2]">
        <X className="h-4.5 w-4.5 text-red-600 stroke-[2.5]" />
      </span>
    );
  }
  return <span className="text-sm text-[#6B7280]">{value}</span>;
}

export default function ComparisonTable() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section
      data-testid="comparison-section"
      className="bg-[#F9FAFB] py-16 md:py-24"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-[#1A1A1A] tracking-tight mb-4">
            {COMPARISON.heading}
          </h2>
          <p className="text-[#6B7280] text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            {COMPARISON.subheading}
          </p>
        </div>

        <div
          ref={ref}
          className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {/* Table for desktop */}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="comparison-table">
              <thead>
                <tr>
                  <th className="text-left text-sm font-semibold text-[#1A1A1A] p-4 md:p-5 bg-gray-50 border-b-2 border-gray-200 w-2/5">
                    Feature
                  </th>
                  <th className="text-center text-sm font-bold text-[#20611B] p-4 md:p-5 bg-[#D8FFB8] border-b-2 border-[#20611B]/20 w-[30%]">
                    {COMPARISON.usLabel}
                  </th>
                  <th className="text-center text-sm font-bold text-red-800 p-4 md:p-5 bg-[#FEE2E2] border-b-2 border-red-200 w-[30%]">
                    {COMPARISON.themLabel}
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.rows.map((row, i) => (
                  <tr
                    key={row.feature}
                    data-testid={`comparison-row-${i}`}
                    className={`border-b border-gray-100 last:border-b-0 ${
                      i % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                    }`}
                  >
                    <td className="text-sm text-[#1A1A1A] p-4 md:p-5 font-medium">
                      {row.feature}
                    </td>
                    <td className="text-center p-4 md:p-5 bg-[#D8FFB8]/20">
                      <div className="flex justify-center">
                        <CellContent value={row.us} />
                      </div>
                    </td>
                    <td className="text-center p-4 md:p-5 bg-[#FEE2E2]/20">
                      <div className="flex justify-center">
                        <CellContent value={row.them} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Callout box */}
        <div
          data-testid="comparison-callout"
          className="mt-8 bg-white border border-[#20611B]/20 rounded-xl p-6 md:p-8 text-center"
        >
          <p className="text-[#1A1A1A] text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
            {COMPARISON.callout}
          </p>
        </div>

        {/* CTA */}
        <div className="text-center mt-8">
          <Link to={BRAND.signupPath}>
            <Button
              data-testid="comparison-cta"
              className="bg-[#D4A84B] hover:bg-[#B88E3B] text-white font-bold h-14 px-10 rounded-md shadow-md hover:shadow-lg transition-all active:scale-95 text-base tracking-wide"
            >
              {COMPARISON.cta}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
